import { Howl } from 'howler';
import { useUIStore } from '@/state/uiStore';
import { seedSilent } from '@/domain/seedContext';

// PRD §14 sound spec. Four cues, with a Web Audio API fallback when files are absent.
// Mock-mode (Playwright) suppresses playback via setMuted(true) and logs every call.

export type SoundId = 'inbound-ring' | 'deposit-chime' | 'confirmation-tap' | 'booking-confirmed';

const SOUND_SOURCES: Record<SoundId, string> = {
  'inbound-ring': '/sounds/inbound-ring.wav',
  'deposit-chime': '/sounds/deposit-chime.wav',
  'confirmation-tap': '/sounds/confirmation-tap.wav',
  'booking-confirmed': '/sounds/booking-confirmed.wav',
};

const SOUND_FALLBACK_FREQ: Record<SoundId, { freq: number; durationSec: number; volume: number }> = {
  'inbound-ring': { freq: 880, durationSec: 1.5, volume: 0.2 },
  'deposit-chime': { freq: 1320, durationSec: 0.3, volume: 0.25 },
  'confirmation-tap': { freq: 660, durationSec: 0.2, volume: 0.15 },
  'booking-confirmed': { freq: 1100, durationSec: 0.4, volume: 0.22 },
};

const howls: Partial<Record<SoundId, Howl>> = {};
let testMode = false;
let auditLog: SoundId[] = [];

function ensureHowls() {
  if (Object.keys(howls).length > 0) return;
  for (const id of Object.keys(SOUND_SOURCES) as SoundId[]) {
    howls[id] = new Howl({
      src: [SOUND_SOURCES[id]],
      preload: false,
      volume: 0.6,
      onloaderror: () => {
        // Fallback to Web Audio synthesis if asset missing.
      },
    });
  }
}

function playFallbackTone(id: SoundId) {
  if (typeof window === 'undefined') return;
  try {
    const Ctx = window.AudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (id === 'inbound-ring') {
      // Australian ring tone: 400 + 425 Hz dual tone, 400ms on / 200ms gap /
      // 400ms on / longer gap. Plays one full cadence so the demo can hear
      // the call ringing without the wav asset.
      playAusRingCadence(ctx);
      return;
    }
    const spec = SOUND_FALLBACK_FREQ[id];
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = spec.freq;
    gain.gain.value = 0;
    osc.connect(gain).connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(spec.volume, now + 0.02);
    gain.gain.linearRampToValueAtTime(0, now + spec.durationSec);
    osc.start(now);
    osc.stop(now + spec.durationSec);
  } catch (err) {
    console.warn('[audio] fallback synthesis failed', err);
  }
}

function playAusRingCadence(ctx: AudioContext) {
  const burst = (startOffset: number, durationSec: number) => {
    const a = ctx.createOscillator();
    const b = ctx.createOscillator();
    const g = ctx.createGain();
    a.type = 'sine';
    b.type = 'sine';
    a.frequency.value = 400;
    b.frequency.value = 425;
    g.gain.value = 0;
    a.connect(g);
    b.connect(g);
    g.connect(ctx.destination);
    const t0 = ctx.currentTime + startOffset;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
    g.gain.linearRampToValueAtTime(0.18, t0 + durationSec - 0.04);
    g.gain.linearRampToValueAtTime(0, t0 + durationSec);
    a.start(t0);
    b.start(t0);
    a.stop(t0 + durationSec);
    b.stop(t0 + durationSec);
  };
  burst(0, 0.4);
  burst(0.6, 0.4);
}

export function playSound(id: SoundId): Howl | null {
  if (seedSilent()) return null;
  auditLog.push(id);
  const muted = useUIStore.getState().muted;
  if (muted || testMode) return null;
  ensureHowls();
  const h = howls[id];
  if (!h) {
    playFallbackTone(id);
    return null;
  }
  try {
    h.play();
  } catch {
    playFallbackTone(id);
  }
  return h ?? null;
}

export function getAudioCalls(): SoundId[] {
  return auditLog.slice();
}

export function resetAudioCalls(): void {
  auditLog = [];
}

export function setTestMode(on: boolean) {
  testMode = on;
}

export function setMuted(muted: boolean) {
  useUIStore.getState().setMuted(muted);
}

export function getSoundIds(): SoundId[] {
  return Object.keys(SOUND_SOURCES) as SoundId[];
}
